import type { ReactNode } from 'react';
import { cx } from '@/lib/cx';
import { Icon, type IconName } from './Icon';
import s from './Misc.module.css';

/** Progress bar (0–100). `complete` turns the fill green (e.g. a production store at 1/1). */
export function Progress({
  value,
  height = 4,
  complete,
  label,
  className,
}: {
  value: number;
  height?: 4 | 6;
  complete?: boolean;
  label?: string;
  className?: string;
}) {
  const v = Math.max(0, Math.min(100, value));
  return (
    <div
      className={cx(s.track, height === 6 && s.track6, className)}
      role="progressbar"
      aria-valuenow={Math.round(v)}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={label}
    >
      <div className={cx(s.fill, complete && s.fillOk)} style={{ width: `${v}%` }} />
    </div>
  );
}

/** IconTile: a 34–44px rounded square in #232327 with an icon, or initials. */
export function IconTile({
  icon,
  initials,
  size = 38,
  iconSize = 17,
  className,
}: {
  icon?: IconName;
  initials?: string;
  size?: number;
  iconSize?: number;
  className?: string;
}) {
  return (
    <div className={cx(s.tile, initials && s.initials, className)} style={{ width: size, height: size }}>
      {icon ? <Icon name={icon} size={iconSize} /> : initials}
    </div>
  );
}

/** Initials of a full name: «Дилноза Каримова» → «ДК». */
export function initialsOf(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0] ?? '')
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

/** Avatar: initials on a #2A2A2E circle (34px in the sidebar, 38px in lists). */
export function Avatar({ name, size = 34, className }: { name: string; size?: number; className?: string }) {
  return (
    <div className={cx(s.avatar, className)} style={{ width: size, height: size }} aria-hidden>
      {initialsOf(name)}
    </div>
  );
}

export type LifecycleStep = { key: string; label: ReactNode };

/**
 * Lifecycle: N steps; the steps before `current` are done, `current` is orange and bold, the rest are future.
 * `tone="ok"` paints done steps green (the domain lifecycle).
 */
export function Lifecycle({
  steps,
  current,
  tone = 'acc',
  label,
  className,
}: {
  steps: LifecycleStep[];
  current: number;
  tone?: 'acc' | 'ok';
  label?: string;
  className?: string;
}) {
  return (
    <ol className={cx(s.life, className)} aria-label={label}>
      {steps.map((st, i) => (
        <li
          key={st.key}
          className={cx(s.step, i < current && s.done, i === current && s.current, tone === 'ok' && s.okTone)}
          aria-current={i === current ? 'step' : undefined}
        >
          <div className={s.bar} />
          <span className={s.name}>{st.label}</span>
        </li>
      ))}
    </ol>
  );
}
